import React, { useState, FormEvent, useEffect, useMemo } from 'react'
import { connect, useDispatch } from 'react-redux'
import { Stack, Paper, Container, TextField } from '@mui/material'
import ProgressButton from '../../Utils/ProgressButton'
import { entityTextField, entitySelectField } from '../../Utils/EntityField'
import { AppState } from '../../../reducers/index'
import { upsertGoal } from '../../../actions/goal/goalUpsert'
import { GoalDetailState } from '../../../reducers/goal/goalDetail'
import Goal from '../../../models/Goal'
import { useTranslation } from 'react-i18next'
import { ISTableTitle } from '../../Utils/ISTable'
import { useHistory, useLocation, useParams } from 'react-router-dom'
import { fetchGoal } from '../../../actions/goal/goalDetail'
import { BackButton } from '../../Utils/BackButton'
import { DesktopDateTimePicker, LocalizationProvider } from '@mui/lab'
import AdapterMoment from '@mui/lab/AdapterMoment'
import moment, { Moment } from 'moment'
import { useThemeContext } from '../../../theme/ThemeContext'
import { personTypeToDescription } from '../../Client/Profile'
import { GOAL_KIND_STANDARD, translateGoalKindDescription, translateStatusGoalDescription, translateTypeOfGoalDescription } from './GoalList'
import { fetchAllProductBrands } from '../../../actions/product/productBrandList'
import { ProductBrandListState } from '../../../reducers/product/productBrandList'
import Selector from '../../Selector'

interface Props {
  goal: GoalDetailState
  productBrand: ProductBrandListState
}

const empty: Goal = {
  id: null,
  from: '',
  to: '',
  personType: '',
  byPersonTypeId: 0,
  brandId: null,
  brandName: '',
  dataAdic: '',
  currency: '',
  goal: 0,
  status: 'ACTIVE',
  typeOfGoal: '',
  minPriceListId: 0,
  maxPriceListId: 9999,
  ownerId: 0,
  ownerName: '',
  goalKind: GOAL_KIND_STANDARD,
  calculationRunId: null,
  updated: null
}

const GoalUpsert: React.FC<Props> = ({ goal, productBrand }) => {
  const [changed, setChanged] = useState<boolean>(false)
  const [goalInfo, setGoalInfo] = useState<Goal>(goal.goal || {
    ...empty
  })
  const { t } = useTranslation(['goal', 'person'])
  const { theme } = useThemeContext()
  const [brand, setBrand] = useState(goal.goal?.brandId === 9999 ? t('others') : goal.goal?.brandName || null)
  const dispatch = useDispatch()
  const params = useParams<{ goalId: string }>()
  const history = useHistory()
  const location = useLocation()

  useEffect(() => {
    if (params.goalId) {
      dispatch(fetchGoal(params.goalId))
    }
  }, [dispatch, params.goalId])


  useEffect(() => {
    dispatch(fetchAllProductBrands())
    // eslint-disable-next-line react-hooks/exhaustive-deps    
  }, [])

  const change = (newGoalInfo: Goal) => {
    setGoalInfo(newGoalInfo)
    setChanged(true)
  }
  const setDataBrand = (description: any): number | null => {
    if (description) {
      setBrand(description)
      return productBrand.productBrands?.find((brand) => brand.description === description)?.id || null
    }
    return null
  }


  const changeGoal = async (event: FormEvent) => {
    event.preventDefault()
    try {
      setChanged(false)
      await dispatch(upsertGoal(goalInfo))
      setChanged(false)
      if (location.state)
        (location.state as any).update = !(location.state as any).update
      history.goBack()
    }
    catch {
    }
  }

  const updateBrand = (e: any) => {
    const description = e.target.value
    if (description === t('others')) {
      setBrand(description)
      setGoalInfo({ ...goalInfo, brandId: 9999 })
      setChanged(true)
    }
    else {
      const brandId = setDataBrand(description)
      if (brandId) {
        setGoalInfo({ ...goalInfo, brandId: brandId })
        setChanged(true)
      }
    }
  }

  const selectorList = (arrayList: any[]) => {
    const list = arrayList?.filter(element => element.active)
    return list ? [...list.map(element => element.description), t('others'), ''] : []
  }

  const updateDate = (field: string, newDate: Moment | null) => {
    if (newDate) {
      const date = newDate.tz("America/Argentina/Buenos_Aires").format()
      setGoalInfo({ ...goalInfo, [field]: date })
      setChanged(true)
    }
    else setGoalInfo({ ...goalInfo, [field]: '' })
  }

  useEffect(() => {
    if (goal.goal) {
      setGoalInfo(params.goalId ? goal.goal :
        { ...empty }
      )
      setBrand(params.goalId ? goal.goal.brandId === 9999 ? t('others') : goal.goal.brandName : null)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [goal.goal, params.goalId])


  const GoalInfoField = useMemo(() => entityTextField(change), [])

  const GoalInfoSelectField = useMemo(() => entitySelectField(change), [])

  const translatePersonTypeDescription = (value: string): string => t(personTypeToDescription(value), { ns: 'person' })

  return (
    <Container>
      <Paper style={{ borderRadius: "10px", margin: "10px" }}>
        <ISTableTitle title={t('goal')} />
        <Paper style={{ padding: "10px" }}>
          <form onSubmit={changeGoal}>
            <Stack display="flex" flexDirection="row" flexGrow={1} >
              <LocalizationProvider dateAdapter={AdapterMoment}>
                <DesktopDateTimePicker
                  label={t("from")}
                  inputFormat="DD/MM/yyyy"
                  ampm={false}
                  //minDate={moment().startOf('month').tz("America/Argentina/Buenos_Aires")}
                  value={goalInfo.from ? moment(new Date(goalInfo.from)) : null}
                  onChange={(date) => updateDate('from', date)}
                  renderInput={(params) => (
                    <TextField
                      {...{
                        ...params,
                      }}
                      required
                      sx={{ marginRight: "10px", marginTop: "16px", minWidth: 250, svg: { fill: theme.typography.button.color } }}
                      size="small"
                    />
                  )}
                />
              </LocalizationProvider>
              <LocalizationProvider dateAdapter={AdapterMoment}>
                <DesktopDateTimePicker
                  label={t("to")}
                  inputFormat="DD/MM/yyyy"
                  ampm={false}
                  minDate={goalInfo.from ? moment(new Date(goalInfo.from)).tz("America/Argentina/Buenos_Aires") : moment().startOf('month').tz("America/Argentina/Buenos_Aires")}
                  value={goalInfo.to ? moment(new Date(goalInfo.to)) : null}
                  onChange={(date) => updateDate('to', date)}
                  renderInput={(params) => (
                    <TextField
                      {...{
                        ...params,
                      }}
                      required
                      sx={{ marginRight: "10px", marginTop: "16px", minWidth: 250, svg: { fill: theme.typography.button.color } }}
                      size="small"
                    />
                  )}
                />
              </LocalizationProvider>
              <GoalInfoSelectField
                key="personType"
                label={t("personType")}
                entity={goalInfo}
                attr="personType"
                menuItems={['C', 'V']}
                size='small'
                textAdapter={translatePersonTypeDescription}
                style={{ minWidth: "200px", marginRight: "10px" }}
                required
              />
              <TextField
                label="Tipo de objetivo"
                value={
                  translateGoalKindDescription(
                    goalInfo.goalKind ??
                    GOAL_KIND_STANDARD
                  )
                }
                size="small"
                disabled
                sx={{
                  minWidth: '220px',
                  marginRight: '10px',
                  marginTop: '16px'
                }}
              />
              <GoalInfoField
                key="byPersonTypeId"
                variant="outlined"
                label={t("byPersonTypeId")}
                entity={goalInfo}
                attr="byPersonTypeId"
                size='small'
                required
              />
            </Stack>
            <Stack display="flex" flexDirection="row" flexGrow={1}>
              <GoalInfoField
                key="dataAdic"
                variant="outlined"
                label={t("dataAdic")}
                entity={goalInfo}
                attr="dataAdic"
                size='small'
                fullWidth
              />
            </Stack>
            <Stack display="flex" flexDirection="row" flexGrow={1}>
              <Selector
                key="brand"
                label={t("brand")}
                menuItems={selectorList(productBrand.productBrands!)}
                onChange={updateBrand}
                value={brand}
                style={{ width: "300px", marginRight: "10px" }}
                size='small'
              />
              <GoalInfoSelectField
                key={"typeOfGoal"}
                variant="outlined"
                label={t("typeOfGoal")}
                entity={goalInfo}
                attr='typeOfGoal'
                menuItems={['$', 'Qty']}
                textAdapter={translateTypeOfGoalDescription}
                size='small'
                style={{ width: '100px', marginRight: "10px" }}
                required
              />
              <GoalInfoField
                key={"goal"}
                variant="outlined"
                label={t("goal")}
                entity={goalInfo}
                attr='goal'
                size="small"
                type='number'
                style={{ width: "300px", marginRight: "10px" }}
                required
              />
              <GoalInfoField
                key={'minPriceListId'}
                variant="outlined"
                label={t("minPriceListId")}
                entity={goalInfo}
                attr={'minPriceListId'}
                style={{ width: "200px", marginRight: "10px" }}
                type='number'
                size="small"
              />
              <GoalInfoField
                key={'maxPriceListId'}
                variant="outlined"
                label={t("maxPriceListId")}
                entity={goalInfo}
                attr={'maxPriceListId'}
                style={{ width: "200px", marginRight: "10px" }}
                type='number'
                size="small"
              />
              <GoalInfoSelectField
                key="status"
                label={t("status")}
                entity={goalInfo}
                attr="status"
                menuItems={['ACTIVE', 'INACTIVE', 'CANCELLED']}
                textAdapter={translateStatusGoalDescription}
                size='small'
                style={{ width: "200px" }}
                required
              />
            </Stack>
            <Stack display="flex" flexDirection='row' paddingTop='10px' justifyContent="space-between">
              <BackButton />
              <ProgressButton state={goal} disabled={!changed}>
                {t('save')}
              </ProgressButton>
            </Stack>
          </form>
        </Paper>
      </Paper>
    </Container >

  )
}

const mapStateToProps = (state: AppState) => ({
  goal: state.goalDetail,
  productBrand: state.productBrandList
})

export default connect<Props, {}, {}, AppState>(mapStateToProps, {})(GoalUpsert)
